import React, { useEffect, useState } from "react";
import Icon from "../Icon";
import {
  fmtCcy,
  fmtUnits,
  formatFriendly,
  isStuck,
  productColor,
} from "../helpers";
import { AuditEvent, ReconRow, FormattedUpload } from "../types";
import StatusPill from "./StatusPill";
import ValidationErrors from "./ValidationErrors";
import ScheduledManager from "./ScheduledManager";
import ReverseControl from "./ReverseControl";

interface Props {
  record: FormattedUpload | null;
  open: boolean;
  onClose: () => void;
  onRetry: (record: FormattedUpload) => void;
  onDownload: (record: FormattedUpload) => void;
  onReplace: (record: FormattedUpload, file: File) => void;
  onReschedule: (record: FormattedUpload, newDate: string) => void;
  onCancelScheduled: (record: FormattedUpload) => void;
  onUploadCorrected: () => void;
  onReverseRequest: (record: FormattedUpload, reason: string) => void;
  onExpedite: (record: FormattedUpload) => void;
  onOpenIssue: (row: ReconRow) => void;
  canEdit: boolean;
  canReschedule: boolean;
  canReverse: boolean;
  canExpedite: boolean;
  events?: AuditEvent[];
}

type Tab = "audit" | "summary" | "errors";

const AuditDrawer: React.FC<Props> = ({
  record,
  open,
  onClose,
  onRetry,
  onDownload,
  onReplace,
  onReschedule,
  onCancelScheduled,
  onUploadCorrected,
  onReverseRequest,
  onExpedite,
  onOpenIssue,
  canEdit,
  canReschedule,
  canReverse,
  canExpedite,
  events,
}) => {
  const [tab, setTab] = useState<Tab>("audit");

  useEffect(() => {
    if (open) setTab("audit");
    const onEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [open, record?.id, onClose]);

  const trailLoading = !!record?.dbId && events === undefined;
  const trail = events ?? [];

  return (
    <>
      <div
        className={`drawer-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
      />
      <aside className={`drawer ${open ? "open" : ""}`} aria-hidden={!open}>
        {record && (
          <>
            <div className="drawer-head">
              <div className="file-ico" style={{ width: 40, height: 40 }}>
                <Icon name="file-spreadsheet" size={20} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  className="drawer-title"
                  style={{ wordBreak: "break-all" }}
                >
                  {record.filename}
                </div>
                <div className="drawer-sub mono">
                  {record.id} · {record.size} · {record.period}
                </div>
                <div style={{ marginTop: 8 }}>
                  <StatusPill status={record.status} />
                </div>
              </div>
              <button className="close" onClick={onClose} aria-label="Close">
                <Icon name="x" size={18} strokeWidth={2.5} />
              </button>
            </div>
            <div className="drawer-body">
              {record.status === "parsing" && (
                <div className={`alert ${isStuck(record) ? "warn" : "info"}`}>
                  <div className="alert-icon">
                    <Icon
                      name={isStuck(record) ? "alert-triangle" : "clock"}
                      size={18}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="alert-title">
                      {isStuck(record)
                        ? "Taking longer than usual"
                        : "Checking file…"}
                    </div>
                    <div className="alert-desc">
                      {isStuck(record)
                        ? "The team has been alerted. You can cancel this upload and try again."
                        : "We’re reading the file and scheduling it. You can leave this page."}
                    </div>
                    {isStuck(record) && canEdit && (
                      <div style={{ marginTop: 10 }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => onCancelScheduled(record)}
                        >
                          <Icon name="x" size={12} strokeWidth={2.5} /> Cancel
                          upload
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {record.status === "rejected" && (
                <div className="alert danger">
                  <div className="alert-icon">
                    <Icon name="alert-circle" size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="alert-title">
                      This file couldn’t be used
                    </div>
                    <ValidationErrors
                      errors={record.validationErrors || []}
                      total={record.validationErrorCount || 0}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => onDownload(record)}
                      >
                        <Icon name="download" size={12} /> Download original
                        file
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={onUploadCorrected}
                      >
                        <Icon name="upload" size={12} /> Upload corrected file
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {record.status === "scheduled" &&
                (() => {
                  return (
                    <ScheduledManager
                      record={record}
                      canEdit={canEdit}
                      canReschedule={canReschedule}
                      canExpedite={canExpedite}
                      onReplace={onReplace}
                      onReschedule={onReschedule}
                      onCancel={onCancelScheduled}
                      onExpedite={onExpedite}
                    />
                  );
                })()}
              {["processing", "completed", "reversing", "reversed"].includes(
                record.status,
              ) && (
                <ReverseControl
                  record={record}
                  canEdit={canEdit}
                  canReverse={canReverse}
                  onReverseRequest={onReverseRequest}
                />
              )}
              <div className="tabs">
                <div
                  className={`tab ${tab === "audit" ? "active" : ""}`}
                  onClick={() => setTab("audit")}
                >
                  History
                </div>
                <div
                  className={`tab ${tab === "summary" ? "active" : ""}`}
                  onClick={() => setTab("summary")}
                >
                  Summary
                </div>
                <div
                  className={`tab ${tab === "errors" ? "active" : ""}`}
                  onClick={() => setTab("errors")}
                >
                  Issues{" "}
                  {(() => {
                    const issuesTotal =
                      (record.issues?.length || 0) + (record.failed || 0);
                    if (!issuesTotal) return null;
                    return (
                      <span style={{ color: "var(--danger)" }}>
                        · {issuesTotal}
                      </span>
                    );
                  })()}
                </div>
              </div>

              {tab === "summary" && (
                <>
                  {record.status === "failed" && (
                    <div className="alert danger">
                      <div className="alert-icon">
                        <Icon name="alert-circle" size={18} />
                      </div>
                      <div>
                        <div className="alert-title">
                          We couldn’t process this file
                        </div>
                        <div className="alert-desc">{record.error}</div>
                      </div>
                    </div>
                  )}
                  <div className="stat-grid">
                    <div className="stat">
                      <div className="stat-label">Total contributions</div>
                      <div className="stat-value">
                        {(record.rows || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="stat">
                      <div className="stat-label">Reconciled</div>
                      <div
                        className="stat-value"
                        style={{ color: "var(--success)" }}
                      >
                        {(record.processed || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="stat">
                      <div className="stat-label">Issues</div>
                      <div
                        className="stat-value"
                        style={{
                          color:
                            (record.issues?.length || 0) + (record.failed || 0)
                              ? "var(--danger)"
                              : "var(--ink)",
                        }}
                      >
                        {(
                          (record.issues?.length || 0) + (record.failed || 0)
                        ).toLocaleString()}
                      </div>
                    </div>
                    <div className="stat">
                      <div className="stat-label">Time taken</div>
                      <div className="stat-value">{record.duration || "—"}</div>
                    </div>
                  </div>
                  <dl className="detail-list">
                    <dt>Upload ID</dt>
                    <dd className="mono">{record.id}</dd>
                    <dt>Products</dt>
                    <dd>{(record.products || []).length} in this file</dd>
                    <dt>Period</dt>
                    <dd>{record.period}</dd>
                    <dt>Uploaded by</dt>
                    <dd>{record.uploadedBy.name}</dd>
                    <dt>Uploaded on</dt>
                    <dd>{formatFriendly(record.uploadedAt, true)}</dd>
                  </dl>

                  {record.products && record.products.length > 0 && (
                    <>
                      <div className="section-label">Each product</div>
                      <div
                        style={{
                          color: "var(--muted)",
                          fontSize: 12,
                          marginTop: -4,
                          marginBottom: 8,
                        }}
                      >
                        Processed means the reconciliation pass ran for those
                        rows. See the Payment reconciliation tab for the
                        per-entry match status against the Transfer Agent.
                      </div>
                      {record.products.map((product, index) => {
                        const isFinished = record.status === "completed";
                        const pct =
                          isFinished && product.rows > 0
                            ? Math.round(
                                (product.processed / product.rows) * 100,
                              )
                            : 0;
                        const isReversed =
                          record.status === "reversed" ||
                          record.status === "reversing";
                        const isFailed =
                          record.status === "failed" ||
                          record.status === "canceled";
                        return (
                          <div key={index} className="product-row">
                            <div style={{ minWidth: 0 }}>
                              <div className="pname">
                                <span
                                  className="product-dot"
                                  style={{ background: productColor(index) }}
                                />
                                <span className="pname-text">
                                  {product.name}
                                </span>
                              </div>
                              <div className="pmeta">
                                {isFinished ? (
                                  <>
                                    <span className="ok">
                                      {product.processed.toLocaleString()}{" "}
                                      processed
                                    </span>
                                    {product.failed > 0 && (
                                      <>
                                        {" "}
                                        ·{" "}
                                        <span className="err">
                                          {product.failed} issues
                                        </span>
                                      </>
                                    )}{" "}
                                    · {product.rows.toLocaleString()} total
                                  </>
                                ) : isReversed ? (
                                  <>
                                    <span style={{ color: "var(--muted)" }}>
                                      Reversed
                                    </span>{" "}
                                    · {product.rows.toLocaleString()} rows
                                  </>
                                ) : isFailed ? (
                                  <>
                                    <span className="err">
                                      Did not reconcile
                                    </span>{" "}
                                    · {product.rows.toLocaleString()} rows
                                  </>
                                ) : (
                                  <>
                                    <span style={{ color: "var(--muted)" }}>
                                      {product.rows.toLocaleString()} rows ·
                                      waiting to reconcile
                                    </span>
                                  </>
                                )}
                              </div>
                              {isFinished && (
                                <div
                                  className="progress"
                                  style={{ marginTop: 8, width: "100%" }}
                                >
                                  <div
                                    className={`progress-bar ${
                                      product.failed > 0 ? "" : "success"
                                    }`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              )}
                            </div>
                            {isFinished && (
                              <div className="pright">
                                <div className="pcount">{pct}%</div>
                                <div className="plabel">Processed</div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}

              {tab === "audit" && (
                <div className="timeline">
                  {trailLoading ? (
                    <div
                      style={{
                        color: "var(--muted)",
                        fontSize: 13,
                        padding: "16px 0",
                      }}
                    >
                      Loading timeline…
                    </div>
                  ) : trail.length === 0 ? (
                    <div
                      style={{
                        color: "var(--muted)",
                        fontSize: 13,
                        padding: "16px 0",
                      }}
                    >
                      No history yet.
                    </div>
                  ) : (
                    trail.map((event, index) => (
                      <div key={index} className={`tl-item ${event.kind}`}>
                        <div className="tl-dot">
                          <Icon
                            name={
                              event.kind === "success"
                                ? "check"
                                : event.kind === "fail"
                                ? "x"
                                : event.kind === "warn"
                                ? "alert-triangle"
                                : "clock"
                            }
                            size={10}
                            strokeWidth={3}
                          />
                        </div>
                        <div className="tl-time">{event.time}</div>
                        <div className="tl-title">{event.title}</div>
                        {event.desc && (
                          <div className="tl-desc">{event.desc}</div>
                        )}
                        <div className="tl-actor">
                          <div className="mini-avatar">
                            {event.actor.initials}
                          </div>
                          {event.actor.name}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {tab === "errors" && (
                <>
                  {(() => {
                    const issues = record.issues || [];
                    const reconFailed = (record.failed || 0) > 0;
                    if (issues.length === 0 && !reconFailed) {
                      return (
                        <div className="empty" style={{ padding: "40px 0" }}>
                          <div
                            className="empty-illustration"
                            style={{ width: 64, height: 64 }}
                          >
                            <Icon
                              name="check-circle"
                              size={28}
                              strokeWidth={1.5}
                            />
                          </div>
                          <div className="empty-title">No issues</div>
                          <div>
                            Every entry from this upload reconciled cleanly.
                          </div>
                        </div>
                      );
                    }
                    return (
                      <>
                        {reconFailed && (
                          <div className="alert danger">
                            <div className="alert-icon">
                              <Icon name="alert-circle" size={18} />
                            </div>
                            <div>
                              <div className="alert-title">
                                {record.failed} sheet
                                {record.failed === 1 ? "" : "s"} failed during
                                reconciliation
                              </div>
                              {record.error && (
                                <div className="alert-desc">{record.error}</div>
                              )}
                            </div>
                          </div>
                        )}
                        {issues.length > 0 && (
                          <div style={{ marginTop: reconFailed ? 12 : 0 }}>
                            <div className="section-label">
                              {issues.length} entr
                              {issues.length === 1 ? "y" : "ies"} need attention
                            </div>
                            {issues.map((issue) => {
                              const row = issue.reconRow;
                              const isPending = row.match === "pending";
                              const diff = issue.diffAmount ?? 0;
                              return (
                                <button
                                  key={
                                    row.statementUploadId +
                                    row.productId +
                                    row.priceDate
                                  }
                                  type="button"
                                  className="issue-row"
                                  onClick={() => onOpenIssue(row)}
                                >
                                  <div className="issue-row-head">
                                    <span
                                      className={`status-pill ${
                                        isPending ? "warn" : "danger"
                                      }`}
                                    >
                                      <Icon
                                        name={
                                          isPending ? "clock" : "alert-triangle"
                                        }
                                        size={11}
                                        strokeWidth={2.5}
                                      />{" "}
                                      {isPending ? "Pending" : "Mismatched"}
                                    </span>
                                    <span className="issue-row-product">
                                      {row.product}
                                    </span>
                                    <span className="issue-row-date">
                                      {formatFriendly(row.priceDate)}
                                    </span>
                                  </div>
                                  <div className="issue-row-detail">
                                    {isPending ? (
                                      <>
                                        No payments were found for this date
                                        when it was reconciled.
                                      </>
                                    ) : (
                                      <>
                                        Phoenix{" "}
                                        <strong>
                                          GHS {fmtCcy(row.amount)}
                                        </strong>
                                        {" · "}
                                        TA{" "}
                                        <strong>
                                          GHS {fmtCcy(row.amountTA)}
                                        </strong>
                                        {" · "}
                                        <span
                                          style={{
                                            color:
                                              diff !== 0
                                                ? "var(--danger)"
                                                : "var(--ink)",
                                            fontWeight: 600,
                                          }}
                                        >
                                          Δ {diff > 0 ? "+" : ""}
                                          {fmtCcy(diff)}
                                        </span>
                                        {row.diffUnits != null &&
                                          row.diffUnits !== 0 && (
                                            <>
                                              {" · "}
                                              <span
                                                style={{
                                                  color: "#b35900",
                                                  fontWeight: 600,
                                                }}
                                              >
                                                Δ units{" "}
                                                {row.diffUnits > 0 ? "+" : ""}
                                                {fmtUnits(row.diffUnits)}
                                              </span>
                                            </>
                                          )}
                                      </>
                                    )}
                                  </div>
                                  {issue.taCheckResult === "not_found" && (
                                    <div className="ta-check-line ta-check-not-found">
                                      <Icon
                                        name="alert-circle"
                                        size={11}
                                        strokeWidth={2.5}
                                      />{" "}
                                      TA has no record of this — flag for review
                                      {issue.taCheckedAt && (
                                        <>
                                          {" · checked "}
                                          {formatFriendly(
                                            issue.taCheckedAt,
                                            true,
                                          )}
                                        </>
                                      )}
                                    </div>
                                  )}
                                  {issue.taCheckResult === "unavailable" && (
                                    <div className="ta-check-line ta-check-unavailable">
                                      <Icon
                                        name="clock"
                                        size={11}
                                        strokeWidth={2.5}
                                      />{" "}
                                      Couldn&rsquo;t reach TA — Refresh to try
                                      again
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
            <div className="drawer-foot">
              {record.status === "failed" && canEdit && (
                <>
                  <button
                    className="btn btn-danger-ghost"
                    onClick={() => onCancelScheduled(record)}
                  >
                    <Icon name="x" size={14} strokeWidth={2.5} /> Cancel this
                    upload
                  </button>
                  <div style={{ flex: 1 }} />
                  <button
                    className="btn btn-primary"
                    onClick={() => onRetry(record)}
                  >
                    <Icon name="refresh" size={14} /> Try again
                  </button>
                </>
              )}
              {["completed", "reversing", "reversed"].includes(
                record.status,
              ) && (
                <>
                  {(record.failed || 0) > 0 && (
                    <button className="btn">
                      <Icon name="download" size={14} /> Issues report
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={() => onDownload(record)}
                  >
                    <Icon name="download" size={14} /> Download statements
                  </button>
                </>
              )}
              {record.status === "processing" && (
                <button className="btn" disabled>
                  <Icon name="clock" size={14} /> Reconciling…
                </button>
              )}
              {record.status === "scheduled" && (
                <span
                  style={{
                    color: "var(--muted)",
                    fontSize: 12.5,
                    marginRight: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Icon name="clock" size={13} /> Will reconcile on{" "}
                  {formatFriendly(record.scheduledFor, true)}
                </span>
              )}
              <button className="btn" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default AuditDrawer;
